/**
 * BuyCreditsModal — Envío CR-1 · página «Cargar créditos» a pantalla completa.
 *
 * Mismo contrato que antes: <BuyCreditsModal isOpen onClose />. La abren
 * TopNavBar, CreditsPanel y LowBalanceBanner sin cambios.
 *
 * Datos reales:
 *   GET  /api/payments/mercadopago/packages?country=AR   → paquetes y precios
 *   POST /api/payments/mercadopago/checkout             → redirige a Mercado Pago
 *   useCreditsStore                                      → saldo del usuario
 *
 * Interacción de las tarjetas: clic en la tarjeta = elegirla; el botón de la
 * tarjeta elegida («Pagar con Mercado Pago») inicia el pago.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCreditsStore } from '../../store/creditsStore';
import { DEFAULT_ASSETS } from '../../lmx/assets';
import {
  accentOf, decoratePackages, fmtInt, formatMoney, outputsFor, savingsCopy,
} from './buyCreditsModel';
import '../../styles/buy-credits-v2.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC = process.env.PUBLIC_URL || '';
// Por ahora Mercado Pago cobra solo en Argentina (en pesos).
const COUNTRY = 'AR';

// ── Contenido editable ─────────────────────────────────────────────
const SPARKS = [[8, 0], [22, 1.1], [35, 2.2], [48, 0.5], [61, 1.7], [73, 0.9], [86, 2.6], [15, 2.9], [55, 3.0]];

const PLANS = [
  { name: 'Creador', price: 'USD 12', storage: '10 GB', points: ['150 créditos por mes', '3 marcas y 5 personajes', 'Packs extra con 10 % menos'] },
  { name: 'Pro', price: 'USD 29', storage: '50 GB', points: ['400 créditos por mes', '10 marcas y 20 personajes', 'Packs extra con 15 % menos'] },
  { name: 'Estudio', price: 'USD 79', storage: '200 GB', points: ['1.200 créditos por mes', 'Marcas y personajes ilimitados', 'Soporte prioritario'] },
];

const CLOUD_POINTS = [
  ['Cómputo de alto rendimiento', 'Cada generación corre en GPUs en la nube, no en tu computadora. Tu equipo sólo necesita un navegador.'],
  ['Desde cualquier lugar', 'Tus proyectos, marcas, personajes y archivos te esperan en cualquier dispositivo, en cualquier parte del mundo.'],
  ['Seguro y trazable', 'Conexión cifrada, y cada generación queda registrada con su modelo, su resultado y su costo.'],
  ['Resguardo permanente', 'Lo que creás se guarda en almacenamiento redundante, con copias en más de un lugar.'],
];

const LUM_MSGS = [
  'Tus gastos están contenidos: el crédito ya incluye la nube.',
  'Tu contenido está seguro y lo abrís desde cualquier lugar.',
  'Cuanto más grande tu carga, menos pagás por crédito.',
];

const DOTS = ['#4ea2ff', '#9b7bff', '#36d39a', '#ff5fa8', '#ffb547'];
const TOOLS = ['Imágenes con IA', 'Editor de video', 'Avatares que hablan', 'Edición de imagen con IA', 'Videos y secuencias sin límite', 'Marcas', 'Personajes consistentes', 'Publicación programada en redes'];
const TOOLS_2 = ['Tu agente desde el celular', 'Stickers', 'Códigos QR', 'Instructivos', 'Películas', 'Trazabilidad de tu contenido', 'Nube y resguardo', 'Pago seguro'];
const WORDS = [['imágenes', '#4ea2ff'], ['videos', '#9b7bff'], ['avatares que hablan', '#36d39a'], ['tu marca', '#ff5fa8'], ['películas', '#ffb547'], ['instructivos', '#4ea2ff'], ['stickers y QR', '#9b7bff']];

// logo: ruta del archivo oficial (kit de prensa de cada marca) cuando se cargue,
// por ejemplo `${PUBLIC}/brand/logos/openai.svg`. Vacío = sólo texto.
const TECH = [
  ['Claude', 'Anthropic'], ['GPT', 'OpenAI'], ['Nano Banana', 'Google'], ['Imagen 4', 'Google'], ['Lyria', 'Google DeepMind'],
  ['ElevenLabs', 'Voz y música'], ['Seedance', 'ByteDance'], ['DreamActor', 'ByteDance'], ['Kling', 'Kuaishou'],
  ['PixVerse', 'Video'], ['Grok Imagine', 'xAI'], ['Qwen', 'Alibaba'], ['FLUX', 'Black Forest Labs'],
  ['DeepSeek', 'Lenguaje'], ['Lipsync-2', 'Sync Labs'], ['Fabric', 'VEED'], ['Bria', 'Imagen'],
  ['Cloudflare', 'Nube y almacenamiento'], ['AWS', 'Infraestructura'], ['Supabase', 'Base de datos'], ['Mercado Pago', 'Pagos seguros'],
].map(([name, by]) => ({ name, by, logo: '' }));

const FAQS = [
  ['¿Qué es un crédito?', 'Es la unidad con la que se paga cada generación. 10 créditos equivalen a USD 1 a precio de lista; en los packs más grandes cada crédito sale más barato.'],
  ['¿Cuántos créditos usa cada creación?', 'Depende del modelo y de la duración del resultado. Antes de generar, cada nodo te muestra el costo exacto.'],
  ['¿Se cobra si una generación falla?', 'No. Si el modelo no puede completar la generación, no se descuenta ningún crédito.'],
  ['¿El contenido lleva marca de agua?', 'No. Las imágenes, videos y audios que generás y exportás salen sin marcas de agua.'],
  ['¿LMX cobra créditos?', 'Las preguntas sobre cómo usar la plataforma son gratis. Cuando LMX produce o edita por vos, descuenta su costo real: en general, alrededor de 1 crédito cada 50 pedidos.'],
  ['¿Dónde se guarda mi contenido?', 'En la nube de Studio Media Labs. Lo encontrás en la Galería y en tus proyectos, y lo descargás cuando quieras.'],
  ['¿Mi contenido vence?', 'En la cuenta gratuita, lo que no usás durante 90 días se libera para hacer lugar, con aviso previo. Con un plan mensual, tu contenido no vence.'],
  ['¿Vencen los créditos?', 'No. Los créditos que comprás quedan en tu cuenta hasta que los uses.'],
  ['¿Cómo pago?', 'Con Mercado Pago, en pesos argentinos, al tipo de cambio del día: tarjeta, dinero en cuenta y los medios que ofrece Mercado Pago.'],
];

// ── Piezas ─────────────────────────────────────────────────────────
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#0d2a20" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m3.5 8.5 3 3 6-7" />
  </svg>
);

const Marquee = ({ items, label, reverse = false, hidden = false }) => (
  <ul className={`bc2-track${reverse ? ' bc2-track-rev' : ''}`} aria-hidden={hidden || undefined} aria-label={hidden ? undefined : label}>
    {[0, 1].map((copy) =>
      items.map((name, i) => (
        <li key={`${copy}-${name}`} aria-hidden={copy === 1 || undefined}>
          <span className="bc2-dot" style={{ width: 7, height: 7, background: DOTS[i % DOTS.length] }} aria-hidden="true" />
          {name}
        </li>
      )),
    )}
  </ul>
);

const CloudSchematic = () => (
  <svg className="bc2-cloud-svg" role="img" viewBox="0 0 1100 470"
    aria-label="Esquema: tus dispositivos se conectan de forma cifrada a la nube de Studio Media Labs, que procesa en GPU, guarda tu contenido con redundancia, hace copias de seguridad y registra cada acción">
    <defs>
      <pattern id="bc2Dots" width="18" height="18" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.2" fill="rgba(255,255,255,.07)" /></pattern>
      <linearGradient id="bc2Core" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#4ea2ff" /><stop offset=".5" stopColor="#9b7bff" /><stop offset="1" stopColor="#36d39a" /></linearGradient>
      <radialGradient id="bc2CoreGlow" cx=".5" cy=".5" r=".5"><stop offset="0" stopColor="rgba(155,123,255,.30)" /><stop offset="1" stopColor="rgba(155,123,255,0)" /></radialGradient>
      <radialGradient id="bc2Fade" cx=".5" cy=".5" r=".62"><stop offset=".55" stopColor="#fff" /><stop offset="1" stopColor="#000" /></radialGradient>
      <mask id="bc2FadeMask"><rect width="1100" height="470" fill="url(#bc2Fade)" /></mask>
    </defs>
    <rect width="1100" height="470" fill="url(#bc2Dots)" mask="url(#bc2FadeMask)" />
    <g fill="none" stroke="rgba(255,255,255,.14)" strokeWidth="1.4">
      <path d="M150 115 C 300 115, 300 214, 432 214" /><path d="M150 235 L 432 235" /><path d="M150 355 C 300 355, 300 256, 432 256" />
      <path d="M668 206 C 780 206, 790 88, 896 88" /><path d="M668 224 C 780 224, 790 186, 896 186" />
      <path d="M668 246 C 780 246, 790 284, 896 284" /><path d="M668 264 C 780 264, 790 382, 896 382" />
    </g>
    <g fill="none" strokeWidth="2.6" strokeLinecap="round">
      <path className="bc2-flow" d="M150 115 C 300 115, 300 214, 432 214" stroke="#4ea2ff" />
      <path className="bc2-flow" d="M150 235 L 432 235" stroke="#4ea2ff" style={{ animationDelay: '-.9s' }} />
      <path className="bc2-flow" d="M150 355 C 300 355, 300 256, 432 256" stroke="#4ea2ff" style={{ animationDelay: '-1.7s' }} />
      <path className="bc2-flow" d="M668 206 C 780 206, 790 88, 896 88" stroke="#9b7bff" style={{ animationDelay: '-.4s' }} />
      <path className="bc2-flow" d="M668 224 C 780 224, 790 186, 896 186" stroke="#36d39a" style={{ animationDelay: '-1.2s' }} />
      <path className="bc2-flow" d="M668 246 C 780 246, 790 284, 896 284" stroke="#36d39a" style={{ animationDelay: '-2s' }} />
      <path className="bc2-flow" d="M668 264 C 780 264, 790 382, 896 382" stroke="#9b7bff" style={{ animationDelay: '-.7s' }} />
    </g>
    <g>
      {[160, 235, 310].map((y) => <circle key={y} cx="292" cy={y} r="13" fill="#0e0e12" stroke="rgba(255,255,255,.22)" />)}
      <g fill="none" stroke="#36d39a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {[159, 234, 309].map((y) => (
          <g key={y}><rect x="286.5" y={y} width="11" height="8" rx="1.5" /><path d={`M289 ${y} v-2.5 a3 3 0 0 1 6 0 v2.5`} /></g>
        ))}
      </g>
    </g>
    <g fill="none" stroke="#eef0f4" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="110" cy="115" r="36" fill="#15161c" stroke="rgba(255,255,255,.16)" />
      <rect x="96" y="104" width="28" height="18" rx="2" /><path d="M90 126 H130" />
      <circle cx="110" cy="235" r="36" fill="#15161c" stroke="rgba(255,255,255,.16)" />
      <rect x="102" y="221" width="16" height="28" rx="3.5" /><path d="M108 244 H112" />
      <circle cx="110" cy="355" r="36" fill="#15161c" stroke="rgba(255,255,255,.16)" />
      <rect x="95" y="346" width="30" height="20" rx="3" /><path d="M104 346 v-4 h12 v4 M95 355 H125" />
    </g>
    <g fill="#9aa0ad" fontFamily="Roboto, sans-serif" fontSize="14" textAnchor="middle">
      <text x="110" y="171">Desde tu casa</text><text x="110" y="291">Desde el celular</text><text x="110" y="411">Con tu cliente</text>
    </g>
    <circle cx="550" cy="235" r="190" fill="url(#bc2CoreGlow)" />
    <circle className="bc2-ring" cx="550" cy="235" r="120" fill="none" stroke="rgba(155,123,255,.35)" strokeWidth="1" />
    <circle className="bc2-ring" cx="550" cy="235" r="120" fill="none" stroke="rgba(78,162,255,.35)" strokeWidth="1" style={{ animationDelay: '2s' }} />
    <rect x="432" y="160" width="236" height="150" rx="30" fill="#13151d" stroke="url(#bc2Core)" strokeWidth="1.6" />
    <path d="M528 213 c-9 0-13-10-5-14 c0-10 11-14 17-8 c4-9 20-9 23 1 c9-1 13 9 7 13 c4 4 0 8-3 8 Z" fill="none" stroke="url(#bc2Core)" strokeWidth="1.8" strokeLinejoin="round" />
    <text x="550" y="250" textAnchor="middle" fill="#eef0f4" fontFamily="SMLMontserrat, Montserrat, Roboto, sans-serif" fontSize="21" fontWeight="800">Studio Media Labs</text>
    <text x="550" y="276" textAnchor="middle" fill="#8fc3ff" fontFamily="Roboto, sans-serif" fontSize="14" fontWeight="500">Cloud</text>
    <g fill="#15161c" stroke="rgba(255,255,255,.16)">
      {[88, 186, 284, 382].map((y) => <circle key={y} cx="926" cy={y} r="30" />)}
    </g>
    <g fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <g stroke="#9b7bff"><rect x="915" y="77" width="22" height="22" rx="3" /><rect x="921" y="83" width="10" height="10" rx="1" /><path d="M920 73 v4 M926 73 v4 M932 73 v4 M920 99 v4 M926 99 v4 M932 99 v4 M911 82 h4 M911 88 h4 M911 94 h4 M937 82 h4 M937 88 h4 M937 94 h4" /></g>
      <g stroke="#36d39a"><ellipse cx="926" cy="176" rx="12" ry="4.5" /><path d="M914 176 v20 c0 2.5 5.4 4.5 12 4.5 s12-2 12-4.5 v-20 M914 186 c0 2.5 5.4 4.5 12 4.5 s12-2 12-4.5" /></g>
      <g stroke="#36d39a"><path d="M926 270 l12 5 v8 c0 8-5.4 13-12 16 c-6.6-3-12-8-12-16 v-8 Z" /><path d="M920.5 284 l4 4 l7-8" /></g>
      <g stroke="#9b7bff"><path d="M916 372 h20 M916 380 h20 M916 388 h12" /><circle cx="936" cy="390" r="4" /></g>
    </g>
    <g fill="#eef0f4" fontFamily="Roboto, sans-serif" fontSize="14">
      <text x="968" y="84" fontWeight="500">Cómputo GPU</text><text x="968" y="102" fill="#9aa0ad">alto rendimiento</text>
      <text x="968" y="182" fontWeight="500">Almacenamiento</text><text x="968" y="200" fill="#9aa0ad">redundante</text>
      <text x="968" y="280" fontWeight="500">Resguardo</text><text x="968" y="298" fill="#9aa0ad">seguro y permanente</text>
      <text x="968" y="378" fontWeight="500">Trazabilidad</text><text x="968" y="396" fill="#9aa0ad">de cada generación</text>
    </g>
  </svg>
);

// ── Página ─────────────────────────────────────────────────────────
const BuyCreditsModal = memo(({ isOpen, onClose }) => {
  const balance = useCreditsStore((s) => s.balance);
  const unlimited = useCreditsStore((s) => s.unlimited);

  const [rawPackages, setRawPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [tab, setTab] = useState('packs');
  const [sel, setSel] = useState(null);
  const [calc, setCalc] = useState('pro');
  const [openFaq, setOpenFaq] = useState(0);
  const [lum, setLum] = useState(0);
  const [word, setWord] = useState(0);

  const scrollRef = useRef(null);
  const cargasRef = useRef(null);
  const closeRef = useRef(null);
  // El padre puede pasar onClose como función nueva en cada render: se guarda
  // en un ref para que el efecto de apertura no se repita (y no resetee la página).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Paquetes reales.
  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/payments/mercadopago/packages?country=${COUNTRY}`)
      .then((res) => { if (alive) setRawPackages(res.data.packages || []); })
      .catch(() => { if (alive) toast.error('No pudimos cargar los packs. Probá de nuevo en un momento.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isOpen]);

  // Al abrir: arriba de todo, foco en «Volver», sin scroll del fondo, Escape cierra.
  useEffect(() => {
    if (!isOpen) return undefined;
    setTab('packs');
    setSel(null);
    setPurchasing(null);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current?.(); };
    window.addEventListener('keydown', onKey);
    const t1 = setInterval(() => setLum((n) => n + 1), 5000);
    const t2 = setInterval(() => setWord((n) => n + 1), 2200);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      clearInterval(t1);
      clearInterval(t2);
    };
  }, [isOpen]);

  const packs = useMemo(() => decoratePackages(rawPackages), [rawPackages]);
  const calcPack = packs.find((p) => p.id === calc) || packs[1] || packs[0] || null;
  const calcCredits = calcPack ? Number(calcPack.credits) : 150;
  const accent = accentOf(calcPack?.id || calc);
  const save = savingsCopy(calcPack);
  const outputs = outputsFor(calcCredits);
  const [wordText, wordColor] = WORDS[word % WORDS.length];

  const goToPacks = useCallback(() => {
    setTab('packs');
    requestAnimationFrame(() => cargasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, []);

  const handlePurchase = async (packageId) => {
    setPurchasing(packageId);
    try {
      const res = await axios.post(`${API}/payments/mercadopago/checkout`, {
        package_id: packageId,
        country_code: COUNTRY,
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('No se pudo iniciar el pago');
        setPurchasing(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo iniciar el pago');
      setPurchasing(null);
    }
  };

  const onCta = (p) => {
    if (!p.buyable || purchasing) return;
    if (sel === p.id) handlePurchase(p.id);
    else setSel(p.id);
  };

  if (!isOpen) return null;

  const balanceText = unlimited
    ? 'Créditos ilimitados'
    : balance != null ? `Tu saldo: ${fmtInt(balance)} créditos` : null;

  // Se dibuja en document.body (portal) y corta la propagación de los clics:
  // la página se abre también desde dentro del panel de créditos, cuyo fondo
  // cierra el panel al hacer clic. Sin esto, cualquier clic en la página
  // llegaba a ese fondo, cerraba el panel y la página desaparecía con él.
  const stop = (e) => e.stopPropagation();

  return createPortal((
    <div
      ref={scrollRef}
      className="bc2-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bc2-title"
      data-testid="buy-credits-overlay"
      onClick={stop}
      onMouseDown={stop}
      onPointerDown={stop}
    >
      <div className="bc2-page" data-testid="buy-credits-modal">
        <div className="bc2-topbar">
          <button ref={closeRef} type="button" className="bc2-close" onClick={onClose} data-testid="buy-credits-close">
            <ArrowLeft size={16} aria-hidden="true" /> Volver al estudio
          </button>
          <span className="bc2-topbar-title">Cargar créditos</span>
        </div>

        <header className="bc2-header">
          <div className="bc2-aurora" aria-hidden="true" />
          <div className="bc2-header-in">
            <div className="bc2-hero">
              {balanceText && <span className="bc2-balance" data-testid="buy-credits-balance">{balanceText}</span>}
              <h1 id="bc2-title" className="bc2-h1 bc2-display">Cargá créditos y seguí creando</h1>
              <p className="bc2-lead">Imágenes, video, voces, música y avatares, sin marcas de agua. Ves el costo antes de generar y, si algo falla, no se cobra.</p>
            </div>
            <figure className="bc2-video">
              <div className="bc2-video-box">
                {/* Sin autoplay ni loop: se baja sólo si la persona toca play
                    (y se escucha con sonido). Hasta entonces, sólo el póster. */}
                <video
                  src={`${PUBLIC}/brand/enrik.mp4`}
                  poster={`${PUBLIC}/brand/enrik-poster.jpg`}
                  playsInline controls preload="none"
                  aria-label="Enrik presenta Studio Media Labs"
                />
              </div>
              <figcaption>Enrik está hecho con Studio Media Labs: personaje, voz y escena.</figcaption>
            </figure>
          </div>
        </header>

        <main className="bc2-main">
          {/* ── Packs / planes ── */}
          <section ref={cargasRef} className="bc2-section bc2-center" aria-labelledby="bc2-cargas" style={{ scrollMarginTop: 72 }}>
            <h2 id="bc2-cargas" className="bc2-h2 bc2-display" style={{ fontSize: 32 }}>¿Cómo querés cargar?</h2>
            <div className="bc2-tabs" role="tablist" aria-label="Forma de carga">
              <button type="button" role="tab" aria-selected={tab === 'packs'} className={`bc2-opt${tab === 'packs' ? ' is-on' : ''}`} onClick={() => setTab('packs')} data-testid="buy-tab-packs">
                <span className="bc2-opt-title">Comprar créditos</span>
                <span className="bc2-opt-sub">Pagás una vez y los usás cuando quieras</span>
              </button>
              <button type="button" role="tab" aria-selected={tab === 'plans'} className={`bc2-opt${tab === 'plans' ? ' is-on' : ''}`} onClick={() => setTab('plans')} data-testid="buy-tab-plans">
                <span className="bc2-opt-title">Plan mensual</span>
                <span className="bc2-opt-sub">Créditos cada mes y tu contenido guardado</span>
              </button>
            </div>

            {tab === 'packs' ? (
              <div className="bc2-packs">
                <div className="bc2-pack-grid" role="radiogroup" aria-label="Elegí un pack">
                  {loading && [0, 1, 2].map((i) => <div key={i} className="bc2-skeleton" aria-hidden="true" />)}
                  {!loading && packs.length === 0 && (
                    <p className="bc2-note" style={{ gridColumn: '1 / -1' }}>No pudimos cargar los packs. Cerrá y volvé a abrir en un momento.</p>
                  )}
                  {!loading && packs.map((p) => {
                    const isSel = sel === p.id;
                    const busy = purchasing === p.id;
                    return (
                      <article
                        key={p.id}
                        className={`bc2-pack${isSel ? ' is-sel' : ''}${p.buyable ? '' : ' is-off'}`}
                        data-testid={`package-${p.id}`}
                      >
                        {SPARKS.map(([left, delay]) => (
                          <span key={left} className="bc2-spark" aria-hidden="true" style={{ left: `${left}%`, animationDelay: `${delay}s` }} />
                        ))}
                        <button
                          type="button"
                          role="radio"
                          className="bc2-hit"
                          aria-checked={isSel}
                          aria-label={`Elegir el pack ${p.name}`}
                          disabled={!p.buyable}
                          onClick={() => setSel(p.id)}
                        />
                        <div className="bc2-layer bc2-pack-top">
                          <div>
                            <h3 className="bc2-pack-name bc2-display">{p.name}</h3>
                            {p.badge && <span className="bc2-badge">{p.badge}</span>}
                          </div>
                          <span className="bc2-check" aria-hidden="true">{isSel && <CheckIcon />}</span>
                        </div>
                        <div className="bc2-layer">
                          <span className="bc2-price-line">
                            {p.usd != null
                              ? <>Pago único de <strong>{formatMoney(p.usd, 'USD')}</strong>, recibís</>
                              : 'Recibís'}
                          </span>
                          <span className="bc2-num bc2-credits bc2-display">
                            <span className="bc2-credits-n">{fmtInt(p.credits)}</span>
                            <span className="bc2-credits-u">créditos</span>
                          </span>
                          <span className="bc2-per">{p.savingsText}</span>
                          {p.amount != null && <span className="bc2-price-local">≈ {formatMoney(p.amount, p.currency || 'ARS', 0)} con Mercado Pago</span>}
                        </div>
                        <ul className="bc2-layer bc2-uses">
                          {p.uses.map((u, i) => (
                            <li key={u.key} style={{ animationDelay: `${i * 70}ms` }}>
                              <span>{u.label}</span>
                              <span className="bc2-num bc2-display">{fmtInt(u.qty)}</span>
                            </li>
                          ))}
                        </ul>
                        <span className="bc2-layer bc2-uses-note">Cada línea es el pack completo en ese tipo de contenido.</span>
                        <button
                          type="button"
                          className="bc2-cta"
                          onClick={() => onCta(p)}
                          disabled={!p.buyable || Boolean(purchasing)}
                          data-testid={`buy-package-${p.id}`}
                        >
                          {busy && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
                          {!p.buyable ? 'No disponible' : busy ? 'Abriendo Mercado Pago…' : isSel ? 'Pagar con Mercado Pago' : 'Cargar créditos'}
                        </button>
                      </article>
                    );
                  })}
                </div>
                <p className="bc2-note">Precios en dólares. Pagás en pesos con Mercado Pago, al cambio del día. Los créditos no vencen.</p>
              </div>
            ) : (
              <div className="bc2-plans">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <span className="bc2-soon">Próximamente</span>
                  <p style={{ margin: 0, color: '#b4b9c4', fontSize: 17, maxWidth: 560 }}>Los planes mensuales todavía no están activos. Mientras tanto, cargás créditos con un pago único.</p>
                </div>
                <div className="bc2-plan-grid">
                  {PLANS.map((pl) => (
                    <article key={pl.name} className="bc2-pack bc2-plan" tabIndex={0}>
                      <div className="bc2-plan-head">
                        <h3 className="bc2-plan-name bc2-display">{pl.name}</h3>
                        <span style={{ fontSize: 13, color: '#9aa0ad' }}>Próximamente</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span className="bc2-plan-price bc2-display">{pl.price}</span>
                        <span className="bc2-muted" style={{ fontSize: 14 }}>por mes</span>
                      </div>
                      <div className="bc2-plan-storage">
                        <strong>{pl.storage} en la nube</strong>
                        <span className="bc2-muted" style={{ fontSize: 14 }}>Tu contenido no vence</span>
                      </div>
                      <ul>{pl.points.map((t) => <li key={t}>{t}</li>)}</ul>
                      <button type="button" className="bc2-plan-btn" disabled>Próximamente</button>
                    </article>
                  ))}
                </div>
                <button type="button" className="bc2-btn" onClick={goToPacks}>Cargar créditos ahora</button>
              </div>
            )}
          </section>

          {/* ── ¿Qué podés crear? ── */}
          {packs.length > 0 && (
            <section className="bc2-section" aria-labelledby="bc2-make">
              <div className="bc2-make-head">
                <h2 id="bc2-make" className="bc2-h2 bc2-display">¿Qué podés crear con {fmtInt(calcCredits)} créditos?</h2>
                <div className="bc2-segs" role="radiogroup" aria-label="Pack">
                  {packs.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      role="radio"
                      aria-checked={calcPack?.id === p.id}
                      className={`bc2-seg${calcPack?.id === p.id ? ' is-on' : ''}`}
                      onClick={() => setCalc(p.id)}
                    >
                      <span className="bc2-dot" style={{ background: accentOf(p.id) }} aria-hidden="true" />
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
              <div
                key={calcPack?.id}
                className="bc2-outputs"
                style={{ background: `radial-gradient(ellipse 60% 120% at 20% 0%, ${accent}1c, transparent 70%)` }}
              >
                {outputs.map((o, i) => (
                  <div key={o.key} className="bc2-out">
                    <span className="bc2-out-q bc2-display" style={{ color: accent, animationDelay: `${i * 70}ms` }}>{fmtInt(o.qty)}</span>
                    <span className="bc2-out-u"><span className="bc2-dot" style={{ background: o.color }} aria-hidden="true" />{o.unit}</span>
                    <span className="bc2-out-h">{o.hint}</span>
                    {i < outputs.length - 1 && <span className="bc2-out-or" aria-hidden="true">o</span>}
                  </div>
                ))}
              </div>
              <div className="bc2-save">
                <div className="bc2-save-txt">
                  <span className="bc2-save-title bc2-display">{save.title}</span>
                  <span>{save.text}</span>
                </div>
                <span className={`bc2-sticker bc2-display${save.hot ? ' is-hot' : ''}`}>{save.sticker}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button type="button" className="bc2-btn" onClick={goToPacks}>Cargar créditos</button>
              </div>
            </section>
          )}

          {/* ── Nube ── */}
          <section className="bc2-section" aria-labelledby="bc2-nube" style={{ gap: 40 }}>
            <div className="bc2-section bc2-center" style={{ gap: 12 }}>
              <h2 id="bc2-nube" className="bc2-h2 bc2-h2-xl bc2-display">Tu estudio corre en nuestra nube</h2>
              <p className="bc2-cloud-lead">Cómputo de alto rendimiento, almacenamiento seguro y acceso desde cualquier lugar del mundo. Todo eso ya está incluido en cada crédito.</p>
            </div>
            <figure style={{ margin: 0 }}><CloudSchematic /></figure>
            <div className="bc2-points">
              {CLOUD_POINTS.map(([title, text]) => (
                <div key={title}><strong className="bc2-display">{title}</strong><span>{text}</span></div>
              ))}
            </div>

            <div className="bc2-band">
              <div className="bc2-lum" aria-hidden="true">
                <img className="bc2-lum-legl" src={DEFAULT_ASSETS.mascot_leg_l} alt="" />
                <img className="bc2-lum-legr" src={DEFAULT_ASSETS.mascot_leg_r} alt="" />
                <img src={DEFAULT_ASSETS.mascot_body_rest} alt="" />
                <img className="bc2-lum-head" src={DEFAULT_ASSETS.mascot_head} alt="" />
              </div>
              <p key={lum} className="bc2-bubble" role="status" aria-live="polite">{LUM_MSGS[lum % LUM_MSGS.length]}</p>
              <div className="bc2-band-txt">
                <h3 className="bc2-band-h bc2-display">Un estudio completo. Una sola cuenta.</h3>
                <p className="bc2-band-p">
                  Creá <span key={word} className="bc2-word" style={{ color: wordColor }}>{wordText}</span>, editalo, compaginalo y publicalo. Desde la plataforma o pidiéndoselo a tu agente desde el celular.
                </p>
                <p className="bc2-band-s">Lo que hoy pagás en varias suscripciones distintas, acá viene junto e incluido en cada crédito.</p>
              </div>
              <div className="bc2-marquee">
                <Marquee items={TOOLS} label="Herramientas incluidas" />
                <Marquee items={TOOLS_2} reverse hidden />
              </div>
              <button type="button" className="bc2-btn" onClick={goToPacks}>Cargar créditos</button>
            </div>

            <div className="bc2-tech-wrap">
              <p className="bc2-tech-title bc2-display">Los mejores modelos e infraestructura del mercado, en un solo lugar</p>
              <div className="bc2-marquee">
                <ul className="bc2-track bc2-track-tech" aria-label="Modelos y tecnologías disponibles">
                  {[0, 1].map((copy) => TECH.map((m) => (
                    <li key={`${copy}-${m.name}`} aria-hidden={copy === 1 || undefined}>
                      {m.logo && <img className="bc2-tech-logo" src={m.logo} alt="" />}
                      <span className="bc2-tech-txt">
                        <span className="bc2-tech-name bc2-display">{m.name}</span>
                        <span className="bc2-tech-by">{m.by}</span>
                      </span>
                    </li>
                  )))}
                </ul>
              </div>
              <p className="bc2-legal">Las marcas mencionadas pertenecen a sus respectivos dueños.</p>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section className="bc2-section bc2-faq" aria-labelledby="bc2-faq">
            <h2 id="bc2-faq" className="bc2-h2 bc2-display" style={{ textAlign: 'center' }}>Preguntas frecuentes</h2>
            <div className="bc2-faq-list">
              {FAQS.map(([q, a], i) => {
                const open = openFaq === i;
                return (
                  <div key={q} className={`bc2-faq-item${open ? ' is-open' : ''}`}>
                    <h3>
                      <button
                        type="button"
                        className="bc2-faq-btn"
                        aria-expanded={open}
                        aria-controls={`bc2-faq-${i}`}
                        onClick={() => setOpenFaq(open ? -1 : i)}
                      >
                        <span>{q}</span>
                        <span className="bc2-faq-ico" aria-hidden="true">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M8 3v10M3 8h10" /></svg>
                        </span>
                      </button>
                    </h3>
                    {open && <p id={`bc2-faq-${i}`} className="bc2-faq-a">{a}</p>}
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── Cierre ── */}
          <section className="bc2-section bc2-center" style={{ gap: 18 }}>
            <h2 className="bc2-h2 bc2-h2-xl bc2-display">Seguí creando</h2>
            <button type="button" className="bc2-btn bc2-btn-lg" onClick={goToPacks}>Cargar créditos</button>
          </section>
        </main>
      </div>
    </div>
  ), document.body);
});

BuyCreditsModal.displayName = 'BuyCreditsModal';
export default BuyCreditsModal;
